import type { NextRequest } from 'next/server';

// === Types & Interfaces ===
import { Routes } from '@/app/routes';
import { tracking } from '@/lib/features/tracking/tracking';
import { AUTH_ERROR_TYPES } from '@/types';
import type { CsrfValidationResult, KnownAuthError, SignInUrlParams } from './types/utils';

/**
 * Authentication utility functions for CSRF protection and sign-in URL generation.
 * These functions are used across the authentication flow to ensure security and proper routing.
 */

/**
 * Validates CSRF token for protecting against Cross-Site Request Forgery attacks.
 * Compares the CSRF token from cookies against the token provided in headers.
 *
 * @param {NextRequest} request - The incoming Next.js request object
 * @returns {CsrfValidationResult} True if CSRF validation passes, false otherwise
 *
 * @example
 * ```ts
 * const isValid = validateCsrf(request);
 * if (!isValid) {
 *   throw new Error('CSRF validation failed');
 * }
 * ```
 */
export const validateCsrf = (request: NextRequest): CsrfValidationResult => {
    const csrfToken = request.cookies.get('next-auth.csrf-token');
    const headerToken = request.headers.get('x-csrf-token');

    // Track missing tokens for debugging
    if (!csrfToken) {
        tracking.errorWithSource(
            new Error('CSRF validation failed: Missing CSRF cookie token'),
            'validateCsrf',
            { url: request.url },
        );
        return false;
    }

    if (!headerToken) {
        tracking.errorWithSource(
            new Error('CSRF validation failed: Missing CSRF header token'),
            'validateCsrf',
            { url: request.url },
        );
        return false;
    }

    const isValid = csrfToken.value === headerToken;
    if (!isValid) {
        tracking.errorWithSource(
            new Error('CSRF validation failed: Token mismatch'),
            'validateCsrf',
            {
                url: request.url,
                cookieToken: csrfToken.value,
                headerToken,
            },
        );
    }

    return isValid;
};

/**
 * Validates and sanitizes a pathname to prevent malicious redirects.
 * Only allows internal pathnames starting with '/' and containing safe characters.
 *
 * @param pathname - The pathname to validate
 * @returns boolean indicating if the pathname is safe
 */
const isValidPathname = (pathname: string): boolean => {
    // Must start with / and not contain protocol or domain
    if (!pathname.startsWith('/')) return false;

    // Prevent directory traversal
    if (pathname.includes('..')) return false;

    // Check for protocol or domain indicators
    if (pathname.includes('://') || pathname.includes('\\')) return false;

    // Only allow safe URL characters
    const safePathRegex = /^[/a-zA-Z0-9_\-.,?=&%+:]+$/;
    return safePathRegex.test(pathname);
};

/**
 * Creates a fully qualified sign-in URL with callback and optional error parameters.
 * Used for redirecting users to the sign-in page while preserving their intended destination.
 *
 * @param {SignInUrlParams} params - Parameters for generating the sign-in URL
 * @returns {URL} Configured URL object for the sign-in page
 *
 * @example
 * ```ts
 * const signInUrl = createSignInUrl({
 *   request,
 *   pathname: '/dashboard',
 *   error: 'SessionExpired'
 * });
 * redirect(signInUrl.toString());
 * ```
 */
export const createSignInUrl = ({ request, pathname, error }: SignInUrlParams): URL => {
    try {
        // Create base sign-in URL using the current domain
        const signInUrl = new URL(Routes.SIGNIN, request.url);

        // Get the full path including query parameters
        const fullPath = request.nextUrl.pathname + request.nextUrl.search;

        // Validate and sanitize the callback pathname (only the pathname part for validation)
        const safePathname = isValidPathname(pathname) ? pathname : '/';

        if (safePathname !== pathname) {
            tracking.errorWithSource(
                new Error(
                    `Invalid callback pathname detected: ${pathname.startsWith('/') ? 'Must start with /' : pathname.includes('..') ? 'Contains directory traversal' : 'Contains unsafe characters'}`,
                ),
                'createSignInUrl',
                {
                    originalPathname: pathname,
                    sanitizedPathname: safePathname,
                    url: request.url,
                },
            );
        }

        // Determine callback URL:
        // – If a valid, custom `pathname` was provided (and differs from the request path), use it
        // – Otherwise, preserve the full current path plus query parameters
        const isCustomPath = isValidPathname(pathname) && pathname !== request.nextUrl.pathname;
        const callbackUrl = isCustomPath ? safePathname : fullPath;
        signInUrl.searchParams.set('callbackUrl', callbackUrl);

        if (error) {
            // Ensure error is a known error type
            const safeError = encodeURIComponent(error as KnownAuthError);
            signInUrl.searchParams.set('error', safeError);
        }

        return signInUrl;
    } catch (error) {
        // Log the error and fallback to base sign-in URL
        tracking.errorWithSource(
            error instanceof Error ? error : new Error('Failed to create sign-in URL'),
            'createSignInUrl',
            { pathname, url: request.url },
        );

        // Fallback to base URL if URL construction fails
        return new URL(Routes.SIGNIN, request.url);
    }
};

export const getErrorMessage = (
    error: Error | string,
): {
    type: string;
    message: string;
} => {
    // Handle errors with response structure (like GraphQL errors)
    const message =
        (error as any)?.response?.errors?.[0]?.extensions?.code ||
        (error as any)?.message ||
        'An unknown error occurred';

    // Check for specific error patterns and return structured responses
    const forbiddenPatterns = ['forbidden', 'access denied', 'unauthorized', 'not authorized'];
    const isForbiddenError = forbiddenPatterns.some((pattern) =>
        message.toLowerCase().includes(pattern),
    );

    if (isForbiddenError) {
        return {
            type: AUTH_ERROR_TYPES.NO_ACCESS,
            message: 'You do not have access to view this page.',
        };
    }

    // Default error response
    return {
        type: 'general_error',
        message: message,
    };
};
